import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { appToast } from '../../../components/AppToast'
import { fetchTestStatistics } from './api/testApi'

export default function TestStatistics() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const testId = searchParams.get('id')

  const [stats, setStats] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!testId) {
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setError('')
    fetchTestStatistics(testId)
      .then((data) => setStats(data))
      .catch((err) => {
        setError(err?.message || 'Failed to load test statistics')
        appToast.error(err?.message || 'Failed to load test statistics')
      })
      .finally(() => setIsLoading(false))
  }, [testId])

  const summary = stats?.summary
  const rankings = stats?.rankings || []
  const distribution = stats?.distribution || []
  const branchStats = stats?.branchStats || []

  const KPI_CARDS = [
    {
      label: 'Attendance',
      value: summary ? `${summary.presentStudents}/${summary.totalStudents}` : '—',
      color: 'bg-blue-600 text-white',
      icon: 'group',
    },
    {
      label: 'Maximum Marks',
      value: summary ? `${summary.maxMarks}` : '—',
      color: 'bg-emerald-600 text-white',
      icon: 'grade',
    },
    {
      label: 'Highest Marks',
      value: summary ? `${summary.highestMarks}` : '—',
      color: 'bg-amber-500 text-white',
      icon: 'trending_up',
    },
    {
      label: 'Average Score',
      value: summary ? `${summary.avgMarks}` : '—',
      color: 'bg-indigo-600 text-white',
      icon: 'analytics',
    },
  ]

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-12">
      {/* ── Header & Navigation ── */}
      <section className="flex flex-col gap-4 border-b border-outline-variant/10 pb-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate('/education/tests')}
              className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-outline-variant/30 bg-surface-container-lowest text-on-surface hover:bg-surface-container-high transition-colors cursor-pointer"
              title="Back to Tests"
            >
              <span className="material-symbols-outlined text-xl">arrow_back</span>
            </button>
            <div>
              <h1 className="font-display text-3xl font-extrabold tracking-tight text-on-surface">
                Test Performance & Analytics
              </h1>
              <p className="mt-1 text-xs text-on-surface-variant">
                Comprehensive score distribution, branch benchmarks, and student rank rosters.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Test Banner Info ── */}
      {summary && (
        <section className="rounded-3xl border border-primary/20 bg-primary/5 px-6 py-4 shadow-xs">
          <div className="flex items-center gap-3">
            <span className="material-symbols-outlined text-2xl text-primary">analytics</span>
            <p className="text-xs sm:text-sm font-medium text-on-surface">
              Performance analysis for Test #{summary.testNo} conducted on{' '}
              <span className="font-bold">{summary.testDate}</span> |{' '}
              Total Evaluated Candidates: <span className="font-bold">{summary.totalStudents}</span>
            </p>
          </div>
        </section>
      )}

      {isLoading ? (
        <div className="rounded-3xl border border-outline-variant/20 bg-surface-container-lowest p-16 text-center text-sm font-medium text-on-surface-variant">
          Loading performance statistics...
        </div>
      ) : error ? (
        <div className="rounded-3xl border border-error/30 bg-error-container p-6 text-sm text-error">
          {error}
        </div>
      ) : (
        <>
          {/* ── KPI Cards Grid ── */}
          <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {KPI_CARDS.map((kpi) => (
              <div
                key={kpi.label}
                className="flex items-center gap-4 rounded-3xl border border-outline-variant/20 bg-surface-container-lowest px-5 py-4 shadow-sm"
              >
                <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${kpi.color}`}>
                  <span className="material-symbols-outlined text-2xl">{kpi.icon}</span>
                </div>
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-on-surface-variant">{kpi.label}</p>
                  <p className="mt-0.5 text-2xl font-black text-on-surface">{kpi.value}</p>
                </div>
              </div>
            ))}
          </section>

          {/* ── Score Distribution & Branch Comparison ── */}
          <section className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* Mark Distribution Brackets */}
            <div className="rounded-3xl border border-outline-variant/20 bg-surface-container-lowest p-6 shadow-sm">
              <h3 className="mb-4 text-base font-bold text-on-surface flex items-center gap-2">
                <span className="material-symbols-outlined text-primary">donut_large</span>
                Score Distribution Brackets
              </h3>
              <div className="flex flex-col gap-3.5">
                {distribution.map((bracket) => (
                  <div key={bracket.name} className="flex flex-col gap-1.5">
                    <div className="flex items-center justify-between text-xs font-semibold">
                      <span className="text-on-surface">{bracket.name}</span>
                      <span className="text-on-surface-variant">
                        {bracket.records} students ({bracket.percentage.toFixed(1)}%)
                      </span>
                    </div>
                    <div className="h-2.5 w-full overflow-hidden rounded-full bg-surface-container-low">
                      <div
                        className="h-full bg-primary transition-all duration-300"
                        style={{ width: `${Math.min(100, bracket.percentage)}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Branch-wise Performance */}
            <div className="rounded-3xl border border-outline-variant/20 bg-surface-container-lowest p-6 shadow-sm">
              <h3 className="mb-4 text-base font-bold text-on-surface flex items-center gap-2">
                <span className="material-symbols-outlined text-primary">domain</span>
                Branch Performance Comparison
              </h3>
              {branchStats.length === 0 ? (
                <div className="flex h-36 items-center justify-center text-xs text-on-surface-variant">
                  No branch comparison data available
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  {branchStats.map((b) => (
                    <div key={b.branchName} className="flex flex-col gap-1 border-b border-outline-variant/10 pb-2.5 last:border-b-0">
                      <div className="flex items-center justify-between text-xs font-semibold">
                        <span className="text-on-surface">{b.branchName}</span>
                        <span className="text-on-surface-variant">
                          Top: {b.highestPct.toFixed(1)}% | Avg: {b.averagePct.toFixed(1)}%
                        </span>
                      </div>
                      <div className="h-2.5 w-full overflow-hidden rounded-full bg-surface-container-low">
                        <div
                          className="h-full bg-emerald-500 transition-all duration-300"
                          style={{ width: `${Math.min(100, b.averagePct)}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>

          {/* ── Student Rank Table ── */}
          <section className="overflow-hidden rounded-3xl border border-outline-variant/20 bg-surface-container-lowest shadow-sm flex flex-col">
            <div className="flex items-center justify-between border-b border-outline-variant/15 px-6 py-4">
              <h2 className="text-base font-bold text-on-surface">Candidate Rank List</h2>
              <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
                {rankings.length} evaluated
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-outline-variant/15 bg-surface-container-low/60 text-xs font-bold uppercase tracking-wider text-on-surface-variant">
                    <th className="px-5 py-3.5">Rank</th>
                    <th className="px-5 py-3.5">Roll No</th>
                    <th className="px-5 py-3.5">Student Name</th>
                    <th className="px-5 py-3.5">Branch</th>
                    <th className="px-5 py-3.5">Marks</th>
                    <th className="px-5 py-3.5">Percentage</th>
                    <th className="px-5 py-3.5 text-center">Branch Rank</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-outline-variant/10">
                  {rankings.map((student, idx) => (
                    <tr
                      key={student.studentId}
                      className={`transition hover:bg-surface-container-low/40 ${
                        idx % 2 === 0 ? 'bg-surface-container-lowest' : 'bg-surface-container-low/20'
                      }`}
                    >
                      <td className="px-5 py-3.5">
                        <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-black ${
                          student.rank === 1 ? 'bg-amber-400 text-amber-950 shadow-xs' : 'bg-primary/10 text-primary'
                        }`}>
                          {student.rank}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 font-mono text-xs text-on-surface">{student.rollNo || '—'}</td>
                      <td className="px-5 py-3.5 font-medium text-on-surface">{student.fullName}</td>
                      <td className="px-5 py-3.5 text-xs text-on-surface-variant">{student.branchName || '—'}</td>
                      <td className="px-5 py-3.5 font-bold text-on-surface">{student.marks}</td>
                      <td className="px-5 py-3.5 font-mono text-xs text-on-surface">{student.percentage.toFixed(2)}%</td>
                      <td className="px-5 py-3.5 text-center">
                        <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-surface-container-high text-xs font-bold text-on-surface">
                          {student.branchRank}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {rankings.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-5 py-16 text-center text-sm text-on-surface-variant">
                        No marks entered for this test yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  )
}
